import { escapeHtml as esc } from './escapeHtml.js';
import { mlvApplyZ } from './mlvZLayers.js';
import { mlvSaveFocus } from './mlvFocusRestore.js';
import { mlvModalHistoryAttach } from '../navigation/mlvModalHistoryBridge.js';

export function openMiniModalOk({ title, message, okLabel = 'OK' }) {
  return new Promise((resolve) => {
    const restoreFocus = mlvSaveFocus();
    const prev = document.getElementById('mlv-mini-ok-modal');
    if (prev) prev.remove();
    const wrap = document.createElement('div');
    wrap.id = 'mlv-mini-ok-modal';
    wrap.className =
      'fixed inset-0 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[1px]';
    mlvApplyZ(wrap, 'miniModal');
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.innerHTML = `
      <div class="w-full max-w-sm rounded-3xl bg-white p-5 shadow-2xl ring-1 ring-slate-200/80">
        <h3 class="font-heading text-lg font-bold text-slate-900">${esc(title)}</h3>
        <p class="mt-2 text-sm leading-relaxed text-slate-600">${esc(message)}</p>
        <button type="button" id="mlv-mini-ok-btn" class="mt-5 w-full rounded-full bg-brand-primary py-3 text-sm font-bold text-white hover:brightness-95">${esc(okLabel)}</button>
      </div>
    `;
    const hist = mlvModalHistoryAttach(() => {
      try {
        restoreFocus();
      } catch (_e) {
        /* ignore */
      }
      wrap.remove();
      document.removeEventListener('keydown', onKey);
      resolve();
    });
    function close() {
      hist.closeFromUi();
    }
    function onKey(e) {
      if (e.key === 'Escape') close();
    }
    wrap.addEventListener('click', (e) => {
      if (e.target === wrap) close();
    });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(wrap);
    wrap.querySelector('#mlv-mini-ok-btn')?.addEventListener('click', close);
    requestAnimationFrame(() => wrap.querySelector('#mlv-mini-ok-btn')?.focus?.({ preventScroll: true }));
  });
}

/** Confirmación sí/no; resuelve `true` si confirma. Usa el mismo bridge de historial que el modal OK (atrás / PWA). */
export function openMiniModalConfirm({
  title,
  paragraphs = [],
  confirmLabel = 'Aceptar',
  cancelLabel = 'Cancelar',
}) {
  return new Promise((resolve) => {
    const restoreFocus = mlvSaveFocus();
    const prev = document.getElementById('mlv-mini-confirm-modal');
    if (prev) prev.remove();
    const wrap = document.createElement('div');
    wrap.id = 'mlv-mini-confirm-modal';
    wrap.className =
      'fixed inset-0 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[1px]';
    mlvApplyZ(wrap, 'miniModal');
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    const body = (Array.isArray(paragraphs) ? paragraphs : [])
      .map((t) => `<p class="mt-2 text-sm leading-relaxed text-slate-600">${esc(String(t || ''))}</p>`)
      .join('');
    wrap.innerHTML = `
      <div class="w-full max-w-sm rounded-3xl bg-white p-5 shadow-2xl ring-1 ring-slate-200/80">
        <h3 class="font-heading text-lg font-bold text-slate-900">${esc(title)}</h3>
        ${body}
        <div class="mt-5 flex flex-col gap-2 sm:flex-row-reverse sm:justify-end">
          <button type="button" id="mlv-mini-confirm-yes" class="w-full rounded-full bg-brand-primary py-3 text-sm font-bold text-white hover:brightness-95 sm:w-auto sm:min-w-[7rem]">${esc(confirmLabel)}</button>
          <button type="button" id="mlv-mini-confirm-no" class="w-full rounded-full border border-slate-300 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto sm:min-w-[7rem]">${esc(cancelLabel)}</button>
        </div>
      </div>
    `;
    let result = false;
    let outcomeSet = false;
    function onKey(e) {
      if (e.key === 'Escape') applyOutcome(false);
    }
    const hist = mlvModalHistoryAttach(() => {
      try {
        restoreFocus();
      } catch (_e) {
        /* ignore */
      }
      wrap.remove();
      document.removeEventListener('keydown', onKey);
      resolve(result);
    });
    function applyOutcome(v) {
      if (outcomeSet) return;
      outcomeSet = true;
      result = v;
      hist.closeFromUi();
    }
    wrap.addEventListener('click', (e) => {
      if (e.target === wrap) applyOutcome(false);
    });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(wrap);
    wrap.querySelector('#mlv-mini-confirm-yes')?.addEventListener('click', () => applyOutcome(true));
    wrap.querySelector('#mlv-mini-confirm-no')?.addEventListener('click', () => applyOutcome(false));
    requestAnimationFrame(() =>
      wrap.querySelector('#mlv-mini-confirm-no')?.focus?.({ preventScroll: true })
    );
  });
}
